import { useState } from "react";
import { Upload, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { getWithExpiry } from "@/utils/token";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { fetchApi } from "@/Store/Action.ts";

interface ModalCancelProps {
  selectedCampaign?: {
    title: string;
    id: number;
    is_famtour: boolean;
  };
  cancelReason: string;
  setCancelReason: (reason: string) => void;
  refetchNoteList?: () => void;
  setSelectedNote?: (note: null) => void;
}

function ModalCancel({
  selectedCampaign,
  cancelReason,
  setCancelReason,
  refetchNoteList,
  setSelectedNote,
}: ModalCancelProps) {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const token = getWithExpiry("token");
  const MySwal = withReactContent(Swal);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const files = Array.from(e.target.files);

    if (uploadedFiles.length + files.length > 5) {
      toast.error("최대 5개의 파일만 업로드할 수 있습니다");
      return;
    }

    const totalSize = [...uploadedFiles, ...files].reduce(
      (sum, file) => sum + file.size,
      0
    );
    if (totalSize > 10 * 1024 * 1024) {
      toast.error("총 파일 크기는 10MB를 초과할 수 없습니다");
      return;
    }

    setUploadedFiles([...uploadedFiles, ...files].slice(0, 5));
  };

  const handleDeleteFile = (index: number) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedFiles);
  };

  const handleProcess = async () => {
    if (!cancelReason) {
      MySwal.fire({
        icon: "error",
        title: "취소 사유를 입력해주세요",
        // timer: 2000,
        // showConfirmButton: false,
        confirmButtonText: "좋아요",
        buttonsStyling: false,
        customClass: {
          confirmButton:
            "bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-full",
        },
      });
      return;
    }

    setIsProcessing(true);
    try {

      type CancelRequestPayload = {
        fam_tour_id?: number;
        reason: string;
        files?: Array<any>;
      };

      let formData: FormData | string;

      if (!selectedCampaign?.is_famtour) {
        const fd = new FormData();
        fd.append("subscription_id", String(selectedCampaign?.id || 0));
        fd.append("reason", cancelReason);
        uploadedFiles.forEach((file) => {
          fd.append("files", file);
        });
        formData = fd;
      } else {
        const jsonPayload: CancelRequestPayload = {
          fam_tour_id: selectedCampaign?.id ?? 0,
          reason: cancelReason,
          ...(uploadedFiles.length > 0 && { files: uploadedFiles }),
        };
        formData = JSON.stringify(jsonPayload);
      }

      const url = selectedCampaign?.is_famtour
        ? "/user/famtour/canceled"
        : "/user/subscription/request-cancel";

      const response = await fetchApi(url, {
        method: "POST",
        needAuth: true,
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "요청 실패");
      }

      await MySwal.fire({
        icon: "success",
        title: "처리가 완료되었습니다",
        text: "확인 버튼을 누르면 새로고침됩니다",
        confirmButtonText: "확인",
        buttonsStyling: false,
        customClass: {
          confirmButton:
            "bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-full",
        },
      });

      refetchNoteList?.();
      setSelectedNote?.(null);
      window.location.reload();
    } catch (error: any) {
      console.error("Error:", error);
      MySwal.fire({
        icon: "error",
        title: "오류 발생",
        text: error.message || "요청 처리 중 오류 발생",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex flex-col text-left p-2 max-w-md mx-auto">
      <h2 className="text-xl font-bold mb-4 border-b pb-4 mt-4 text-center">
        [ 캠페인 취소 ]
      </h2>

      {selectedCampaign && (
        <p className="text-center font-bold  text-md mb-2">
          {selectedCampaign.title}
        </p>
      )}
      <p className="text-[11px] text-red-400 my-2 pb-1 pt-1">
        {/* <span className="text-black font-bold">
            취소 사유를 입력해주세요.
        </span> */}
 
개인 사유는 패널티 발생하며, 업체 사유는 캡처 필수입니다.
      </p>
      <textarea
        className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 border-gray-400 focus:ring-red-500 text-sm text-gray-800 mb-2"
        rows={3}
        placeholder="취소 사유를 입력해주세요"
        value={cancelReason}
        onChange={(e) => setCancelReason(e.target.value)}
      />

      <p className="text-[11px]  my-2 pb-2">
        최대 5개의 파일만 업로드할 수 있으며, 첨부 파일의 총 크기는 10MB
        미만이어야 합니다. 허용되는 파일 형식: png, jpg, pptx 등
      </p>

      <label className="flex items-center justify-between pe-2 ps-3 py-1.5 border border-gray-400 rounded-full cursor-pointer text-sm text-gray-400 mb-4 bg-[#f9f9f9]">
        참고용으로 추가 문서를 업로드하세요.
        <input
          type="file"
          multiple
          accept=".png,.jpg,.jpeg,.pptx"
          className="hidden"
          onChange={handleFileChange}
          disabled={uploadedFiles.length >= 5}
        />
        <span className="text-white bg-[#ff7158] px-4 py-2 rounded-full text-xs font-semibold">
          {uploadedFiles.length >= 5 ? "최대 파일 수 도달" : "파일 선택"}
        </span>
      </label>

      <div className="space-y-2 mb-4 max-h-36 overflow-y-auto">
        {uploadedFiles.map((file, index) => (
          <div
            key={`${file.name}-${index}`}
            className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-md border"
          >
            <div className="flex items-center gap-2 text-sm text-gray-800">
              <Upload size={20} />
              <div className="flex flex-col ps-1">
                <span className="truncate max-w-[240px]">{file.name}</span>
                <span className="text-[11px] text-gray-400">
                  {(file.size / 1024).toFixed(0)}kb
                </span>
              </div>
            </div>
            <button
              onClick={() => handleDeleteFile(index)}
              aria-label="파일 삭제"
            >
              <Trash2 size={20} className="text-red-500" />
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="w-full bg-[#fa5a46] hover:bg-red-600 text-white py-3 rounded-full font-semibold mt-2 disabled:opacity-50"
        onClick={handleProcess}
        disabled={isProcessing || !cancelReason}
      >
        {isProcessing ? "처리 중..." : "취소요청하기"}
      </button>
    </div>
  );
}

export default ModalCancel;
